import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useBilling } from "../context/BillingContext";
import {
  LayoutDashboard, BookOpenText, Sparkles, ClipboardList,
  Users, UserSquare, CalendarDays, Building2, LogOut, X, Pencil, Check,
  CreditCard, UserCog, Crown, ArrowUpRight, MessageSquareText, LifeBuoy
} from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard", editable: false, feature: null },
  { to: "/musings", icon: BookOpenText, key: "musings", editable: false, feature: "musings" },
  { to: "/content", icon: Sparkles, key: "content", editable: false, feature: "ai_studio" },
  { to: "/prompts", icon: ClipboardList, key: "prompts", editable: false, feature: "prompts" },
  { to: "/team", icon: Users, key: "team", editable: true, feature: "team_db" },
  { to: "/customers", icon: UserSquare, key: "customers", editable: true, feature: "customers_db" },
  { to: "/schedule", icon: CalendarDays, key: "schedule", editable: false, feature: "scheduler" },
  { to: "/profile", icon: Building2, key: "profile", editable: true, feature: null },
];

function NavItem({ item, onClick }) {
  const { label, renameLabel } = useSettings();
  const { canUse, isOrgMember } = useBilling();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  // Only hide nav for org members lacking the feature toggle.
  // Locked-by-plan items stay visible so the user can discover the upgrade flow.
  if (item.feature && isOrgMember && !canUse(item.feature)) return null;

  const save = () => {
    const v = draft.trim();
    if (v) renameLabel(item.key, v);
    setEditing(false);
  };
  return (
    <div className="flex items-stretch group">
      <NavLink
        to={item.to}
        onClick={onClick}
        data-testid={`nav-${item.key}`}
        className={({ isActive }) =>
          `flex-1 flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 ${
            isActive ? "border-[#0033FF] bg-zinc-50 text-zinc-900" : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
          }`
        }
      >
        <item.icon className="w-4 h-4" strokeWidth={2} />
        {editing ? (
          <input data-testid={`nav-rename-input-${item.key}`} autoFocus value={draft}
            onChange={(e) => setDraft(e.target.value)} onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            className="flex-1 bg-white border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:border-[#0033FF]"
            onClick={(e) => e.preventDefault()} />
        ) : (
          <span className="flex-1">{label(item.key)}</span>
        )}
      </NavLink>
      {item.editable && !editing && (
        <button data-testid={`nav-rename-${item.key}`}
          onClick={(e) => { e.preventDefault(); setDraft(label(item.key)); setEditing(true); }}
          className="px-2 text-zinc-400 hover:text-[#0033FF] opacity-0 group-hover:opacity-100" title="Rename">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      {editing && <button onClick={save} className="px-2 text-[#0033FF]"><Check className="w-4 h-4" /></button>}
    </div>
  );
}

function PlanBadge() {
  const { plan, isOrgAdmin, isOrgMember } = useBilling();
  const tone = plan === "enterprise" ? "bg-zinc-900 text-[#FFB800]" : plan === "executive" ? "bg-[#0033FF] text-white" : "bg-zinc-100 text-zinc-700";
  const label = plan?.toUpperCase() || "ENTRY";
  return (
    <NavLink to="/billing" data-testid="plan-badge" className="flex items-center justify-between gap-2 px-6 py-3 border-t border-zinc-200 hover:bg-zinc-50">
      <div className="flex items-center gap-2">
        {plan === "enterprise" && <Crown className="w-3.5 h-3.5 text-[#FFB800]" />}
        <div className={`text-[10px] tracking-[0.25em] uppercase font-bold px-2 py-1 ${tone}`}>{label}</div>
        {isOrgAdmin && <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500">Admin</span>}
        {isOrgMember && <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-500">Seat</span>}
      </div>
      <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400" />
    </NavLink>
  );
}

function SidebarContent({ onNavClick }) {
  const { user, logout } = useAuth();
  const { plan, isOrgAdmin } = useBilling();
  const nav = useNavigate();
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo size={36} />
          <div>
            <div className="text-lg font-black tracking-tighter leading-none">VIZIONCAST</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">Leader OS</div>
          </div>
        </div>
        {onNavClick && <button onClick={onNavClick} className="lg:hidden" data-testid="sidebar-close"><X className="w-5 h-5" /></button>}
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV.map((item) => <NavItem key={item.to} item={item} onClick={onNavClick} />)}
        {plan === "enterprise" && isOrgAdmin && (
          <NavLink to="/seats" onClick={onNavClick} data-testid="nav-seats"
            className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 ${isActive ? "border-[#0033FF] bg-zinc-50 text-zinc-900" : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}>
            <UserCog className="w-4 h-4" /><span>Team Seats</span>
          </NavLink>
        )}
        <button onClick={() => { onNavClick?.(); window.dispatchEvent(new Event("vizi:open")); }} data-testid="nav-ask-vizi"
          className="w-full flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900">
          <LifeBuoy className="w-4 h-4" /><span>Help · Ask Vizi</span>
        </button>
        <NavLink to="/billing" onClick={onNavClick} data-testid="nav-billing"
          className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 ${isActive ? "border-[#0033FF] bg-zinc-50 text-zinc-900" : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}>
          <CreditCard className="w-4 h-4" /><span>Billing & Plans</span>
        </NavLink>
        {user?.is_owner && (
          <NavLink to="/owner" onClick={onNavClick} data-testid="nav-owner"
            className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 ${isActive ? "border-[#0033FF] bg-zinc-50 text-zinc-900" : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}>
            <MessageSquareText className="w-4 h-4" /><span>Owner HQ</span>
          </NavLink>
        )}
      </nav>
      <PlanBadge />
      <div className="border-t border-zinc-200 p-4">
        <div className="text-xs text-zinc-500 mb-2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
        <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold tracking-wider uppercase border border-zinc-300 hover:bg-zinc-900 hover:text-white hover:border-zinc-900">
          <LogOut className="w-3.5 h-3.5" />Sign Out
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({ open, onClose }) {
  return (
    <>
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-zinc-200 bg-white h-screen sticky top-0 flex-col" data-testid="sidebar">
        <SidebarContent />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar">
          <div className="absolute inset-0 bg-zinc-900/60" onClick={onClose} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl overflow-y-auto">
            <SidebarContent onNavClick={onClose} />
          </div>
        </div>
      )}
    </>
  );
}
